import Title from '@cohort/components-xps/components/Title';
import {
  MAX_MEMBER_HANDLE_LENGTH,
  MemberHandleSchema,
  MIN_MEMBER_HANDLE_LENGTH,
} from '@cohort/shared/schema/common/space';
import {isCohortError} from '@cohort/shared-frontend/common/errors';
import {classNames} from '@cohort/shared-frontend/utils/classNames';
import PrimaryButton from '@cohort/wallet/components/button/PrimaryButton';
import SecondaryButton from '@cohort/wallet/components/button/SecondaryButton';
import {InputV2} from '@cohort/wallet/components/forms/v2/Input';
import {useCohortMutation} from '@cohort/wallet/hooks/api/Query';
import {userKeys} from '@cohort/wallet/hooks/api/User';
import {useApi} from '@cohort/wallet/hooks/useApi';
import useScreenSize from '@cohort/wallet/hooks/useScreenSize';
import useThemeContext from '@cohort/wallet/hooks/useThemeContext';
import {UserApi} from '@cohort/wallet/lib/Endpoints';
import {trackError} from '@cohort/wallet/lib/Tracking';
import type {UserWDto} from '@cohort/wallet-schemas/user';
import {useQueryClient} from '@tanstack/react-query';
import type {FC} from 'react';
import {Fragment, useState} from 'react';
import {useTranslation} from 'react-i18next';

type Props = {
  user: UserWDto;
};

export const MemberHandleForm: FC<Props> = ({user}) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const [memberHandle, setMemberHandle] = useState<string>(user!.membershipPass.memberHandle);
  const [error, setError] = useState<string | undefined>(undefined);
  const [showMemberHandleEdit, setShowMemberHandleEdit] = useState(false);
  const {hasDarkBg, accentColor} = useThemeContext();
  const queryClient = useQueryClient();
  const patchUser = useApi(UserApi.patchUser);
  const {t} = useTranslation('pages', {keyPrefix: 'space.settingsPage'});
  const {isMobile} = useScreenSize();

  const handleMemberHandleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const {value} = e.target;

    const parseRes = MemberHandleSchema.safeParse(value);
    if (parseRes.success === false) {
      switch (parseRes.error.issues[0]?.code) {
        case 'too_big':
          setError(t('memberHandleMaxLengthError', {maxLength: MAX_MEMBER_HANDLE_LENGTH}));
          break;
        case 'too_small':
          setError(t('memberHandleMinLengthError', {minLength: MIN_MEMBER_HANDLE_LENGTH}));
          break;
        default:
          setError(t('invalidMemberHandleError'));
          break;
      }
      setMemberHandle(value);
      return;
    }

    setMemberHandle(value);
    setError(undefined);
  };

  const {mutate: updateSpace} = useCohortMutation({
    mutationFn: async () => {
      const data = {
        membershipPass: {
          memberHandle,
        },
      };
      return patchUser(data);
    },
    notifySuccessMessage: t('notificationSuccess'),
    onSuccess: () => {
      queryClient.invalidateQueries(userKeys.getUser());
      setShowMemberHandleEdit(false);
      setError(undefined);
    },
    onError: err => {
      if (isCohortError(err) && err.code === 'membership-pass.duplicated-member-handle') {
        setError(t('duplicatedMemberHandleError'));
      } else {
        trackError(err);
      }
    },
  });

  const handleEditMemberHandleClick = (): void => {
    if (!showMemberHandleEdit) {
      // Todo: add tracking here.
    } else {
      // Todo: add tracking here.
      setError(undefined);
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      setMemberHandle(user!.membershipPass.memberHandle);
    }
    setShowMemberHandleEdit(!showMemberHandleEdit);
  };

  const renderInfoText = (): JSX.Element => {
    if (error === undefined) {
      return <Fragment />;
    }

    return <p className="mt-1.5 text-sm text-red-500">{error}</p>;
  };

  return (
    <div
      className={classNames(
        'flex w-full justify-between md:flex-row',
        showMemberHandleEdit && 'flex-col'
      )}
    >
      <div className="mt-2 w-full md:w-2/5">
        <Title content={t('username')} classes="text-lg" />
        {showMemberHandleEdit ? (
          <Fragment>
            <form
              className="flex w-full flex-col space-y-3 pt-1 md:flex-row md:space-x-2 md:space-y-0"
              onSubmit={e => {
                e.preventDefault();
                updateSpace();
              }}
            >
              <InputV2
                testId="space-settings-member-handle-input"
                type="text"
                name="Member handle"
                accentColor={accentColor}
                placeholder={user.email.split('@')[0]}
                hasDarkBg={hasDarkBg}
                value={memberHandle}
                className="w-full"
                onChange={handleMemberHandleChange}
                max={MAX_MEMBER_HANDLE_LENGTH}
              />
              <PrimaryButton
                type="submit"
                testId="space-settings-member-handle-save-btn"
                text={t('save')}
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                disabled={memberHandle === user!.membershipPass.memberHandle || error !== undefined}
                tracking={{namespace: 'settings.saveMemberHandle'}}
              />
            </form>
            {!isMobile && renderInfoText()}
          </Fragment>
        ) : (
          <div className={classNames('text-sm', hasDarkBg ? 'text-white/60' : 'text-black/60')}>
            @{memberHandle}
          </div>
        )}
      </div>

      <div>
        <SecondaryButton
          text={showMemberHandleEdit ? t('cancel') : t('edit')}
          onClick={handleEditMemberHandleClick}
          testId="space-settings-edit-btn"
          type="button"
          className={classNames(showMemberHandleEdit && isMobile && 'mt-2 w-full')}
          {...(!isMobile
            ? {size: 'small'}
            : !showMemberHandleEdit
              ? {size: 'small'}
              : {size: 'default'})}
          tracking={{namespace: 'settings.editMemberHandle'}}
        />
        {isMobile && renderInfoText()}
      </div>
    </div>
  );
};
